import { Button, Modal } from '@evaneos/b2b-ui/legacy';
import cx from 'classnames';
import { useIntl } from 'react-intl';

import { Alert, Typography } from '@evaneos/b2b-ui';

import { Loader } from '../../../design-system/Loader';
import { DissatisfactionCategories } from '../../../resources/dissatisfaction-categories/types';
import { TravellerTrip } from '../../../resources/dossier/types';
import { TripInformations } from '../../../resources/testimonial/components/TripInformations';
import { Testimonial } from '../../../resources/testimonial';

import s from './TestimanialModal.module.css';

type Props = {
    modalOpened: boolean;
    openModal: (open: boolean) => void;
    trip: TravellerTrip;
    testimonial: Testimonial | null;
    dissatisfactionCategories: DissatisfactionCategories | null;
    isLoading?: boolean;
    tripBudget?: string;
    error?: Error | null;
};

export const TestimonialModal = ({
    modalOpened,
    openModal,
    trip,
    testimonial,
    dissatisfactionCategories,
    isLoading = false,
    tripBudget,
    error,
}: Props): JSX.Element => {
    const { formatMessage } = useIntl();
    return (
        <Modal
            isOpen={modalOpened}
            mainButton={
                <Button
                    onClick={() => {
                        openModal(false);
                    }}
                >
                    {formatMessage({
                        id: 'global.close',
                    })}
                </Button>
            }
            title={formatMessage({
                id: 'dossier.information.testimonial_modal.title',
            })}
            onRequestClose={() => openModal(false)}
        >
            {trip && (
                <TripInformations
                    informations={{
                        destination: trip.destination,
                        year: trip.year,
                        budget: tripBudget,
                    }}
                />
            )}
            {isLoading && <Loader className={s.loader} />}
            {!isLoading && error && (
                <Alert severity="error" sx={{ maxWidth: '100%', mt: 1, display: 'flex' }}>
                    <Typography variant="body1" color="error">
                        {formatMessage({
                            id: 'global.error.failed_fetch',
                        })}
                    </Typography>
                </Alert>
            )}
            {!isLoading && !error && (
                <>
                    <TravellerTestimonial
                        dissatisfactionCategories={dissatisfactionCategories}
                        testimonial={testimonial}
                    />
                </>
            )}
        </Modal>
    );
};

function EmptyTestimonial() {
    const { formatMessage } = useIntl();
    return (
        <div className={s.empty}>
            <p>
                {formatMessage({
                    id: `dossier.information.testimonial_modal.empty.traveller`,
                })}
            </p>
        </div>
    );
}

function TravellerTestimonial({
    dissatisfactionCategories,
    testimonial,
}: Readonly<{
    dissatisfactionCategories: DissatisfactionCategories | null;
    testimonial: Testimonial | null;
}>) {
    const { formatMessage } = useIntl();

    if (!testimonial && (!dissatisfactionCategories || dissatisfactionCategories?.length === 0))
        return <EmptyTestimonial />;

    return (
        <div className={s.scrollingContent}>
            {dissatisfactionCategories && dissatisfactionCategories.length > 0 && (
                <>
                    <p className={s.subtitle}>
                        {formatMessage({
                            id: 'dossier.information.testimonial_modal.dissatisfaction.title',
                        })}
                    </p>
                    <div className={s.dissatifactions}>
                        {dissatisfactionCategories.map((category) => (
                            <span key={category} className={s.label}>
                                {formatMessage({
                                    id: `dossier.information.testimonial_modal.dissatisfaction.labels.${category}`,
                                })}
                            </span>
                        ))}
                    </div>
                </>
            )}

            {testimonial && (
                <>
                    {testimonial.phrasePlus && (
                        <>
                            <p className={cx(s.subtitle)}>
                                {formatMessage({
                                    id: 'dossier.information.testimonial_modal.phrase_plus',
                                })}
                            </p>
                            <p className="body">{testimonial.phrasePlus}</p>
                        </>
                    )}

                    {testimonial.phraseLess && (
                        <>
                            <p className={cx(s.subtitle)}>
                                {formatMessage({
                                    id: 'dossier.information.testimonial_modal.phrase_less',
                                })}
                            </p>
                            <p className="body">{testimonial.phraseLess}</p>
                        </>
                    )}

                    <p className={cx(s.subtitle)}>
                        {formatMessage({
                            id: 'dossier.information.testimonial_modal.testimonial',
                        })}
                    </p>
                    <p className="body">{testimonial.tripDescription}</p>
                </>
            )}
        </div>
    );
}
